import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const { t } = useTranslation();
  const letterVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 2.3 + i * 0.05,
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
  };

  const titleLine1 = t('hero.title1');
  const titleLine2 = t('hero.title2');

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-stone-100"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-stone-100 via-stone-50 to-stone-100"></div>
        
        {/* Animated floating particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-gold/30 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              y: [null, Math.random() * -200 - 100],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 5 + 5,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: 'linear',
            }}
          />
        ))}

        {/* Animated geometric lines */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#c9a962" stopOpacity="0.4"/>
              <stop offset="100%" stopColor="#c9a962" stopOpacity="0.1"/>
            </linearGradient>
          </defs>
          
          {/* Animated lines */}
          <motion.line
            x1="60%" y1="0" x2="100%" y2="60%"
            stroke="url(#goldGrad)"
            strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 2, delay: 2.5, ease: "easeOut" }}
          />
          <motion.line
            x1="70%" y1="0" x2="100%" y2="40%"
            stroke="url(#goldGrad)"
            strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 2, delay: 2.7, ease: "easeOut" }}
          />
          <motion.line
            x1="50%" y1="100%" x2="100%" y2="50%"
            stroke="url(#goldGrad)"
            strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 2, delay: 2.9, ease: "easeOut" }}
          />
          
          {/* Animated circles */}
          <motion.circle
            cx="85%" cy="35%" r="4"
            fill="#c9a962"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.3 }}
            transition={{ duration: 0.5, delay: 3 }}
          />
          <motion.circle
            cx="95%" cy="55%" r="3"
            fill="#c9a962"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.2 }}
            transition={{ duration: 0.5, delay: 3.2 }}
          />
        </svg>

        {/* Animated wave */}
        <motion.div
          className="absolute right-0 top-1/3 w-1/2 h-64 opacity-40"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 0.4, x: 0 }}
          transition={{ duration: 1.5, delay: 2.8 }}
        >
          <svg viewBox="0 0 400 200" className="w-full h-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#c9a962" stopOpacity="0"/>
                <stop offset="50%" stopColor="#c9a962" stopOpacity="0.8"/>
                <stop offset="100%" stopColor="#c9a962" stopOpacity="0"/>
              </linearGradient>
            </defs>
            <motion.path
              d="M0,100 Q100,50 200,100 T400,100"
              fill="none"
              stroke="url(#waveGrad)"
              strokeWidth="2"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: 3, ease: "easeInOut" }}
            />
            <motion.path
              d="M0,110 Q100,60 200,110 T400,110"
              fill="none"
              stroke="url(#waveGrad)"
              strokeWidth="1.5"
              opacity="0.7"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: 3.2, ease: "easeInOut" }}
            />
          </svg>
        </motion.div>

        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.02] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIi8+PC9zdmc=')]"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-24">
        <div className="max-w-3xl">
          {/* Frosted Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 2.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-white/60 backdrop-blur-md border border-gold/20 shadow-lg shadow-gold/5"
          >
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-2 rounded-full bg-gold"
            />
            <span className="text-xs font-medium text-navy/70 tracking-wider uppercase">
              {t('hero.badge')}
            </span>
            <Sparkles className="w-3 h-3 text-gold" />
          </motion.div>

          {/* Main Headline - Letter by letter animation */}
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-navy font-light leading-[1.1] mb-8">
            <span className="block overflow-hidden">
              {titleLine1.split('').map((letter, i) => (
                <motion.span
                  key={i}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={letterVariants}
                  className="inline-block"
                >
                  {letter === ' ' ? '\u00A0' : letter}
                </motion.span>
              ))}
            </span>
            <span className="block mt-2 overflow-hidden">
              {titleLine2.split('').map((letter, i) => (
                <motion.span
                  key={i}
                  custom={i + titleLine1.length}
                  initial="hidden"
                  animate="visible"
                  variants={letterVariants}
                  className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-dark"
                >
                  {letter === ' ' ? '\u00A0' : letter}
                </motion.span>
              ))}
            </span>
          </h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 3.2 }}
            className="text-lg md:text-xl text-navy/60 font-light leading-relaxed mb-12 max-w-xl"
          >
            {t('hero.description')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 3.4 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <motion.a
              href="#services"
              className="liquid-glass-button px-8 py-4 font-light tracking-wide"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="flex items-center justify-center gap-3">
                {t('hero.cta1')}
                <motion.span
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowDown className="w-4 h-4" />
                </motion.span>
              </span>
            </motion.a>
            <motion.a
              href="#contact"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 liquid-glass text-navy font-light tracking-wide rounded-xl hover:bg-white/50 transition-all duration-300"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('hero.cta2')}
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 4 }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-xs text-navy/40 tracking-widest uppercase">{t('hero.scroll')}</span>
          <div className="w-px h-12 bg-gradient-to-b from-gold/50 to-transparent"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
